import React, { useContext, useState, useEffect, useMemo } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import TopBar, { TOP_BAR_HEIGHT } from './ui/TopBar';
import {
  Business,
  Lock,
  Person,
  AccountBalance,
  Palette,
  Label,
  List,
  Groups,
  AccountCircle,
  Email,
  SettingsInputAntenna,
  Description as DescriptionIcon,
  NotificationsNone,
  Https,
  Code as CodeIcon,
} from '@mui/icons-material';
import { AuthContext } from './AuthContext';
import { SettingsFormProvider, useSettingsForm } from './SettingsFormContext';
import CompanyInformation from './CompanyInformation';
import AccessPermissions from './AccessPermissions';
import TeamOrganization from './TeamOrganization';
import Profile from './Profile';
import EmailCalendarSettings from './EmailCalendarSettings';
import Sidebar from './Sidebar';
import IntegrationsSettings from './IntegrationsSettings';
import QuickBooksManagePage from './settings/QuickBooksManagePage';
import AddTagPage from './AddTagPage';
import AccountManagement from './settings/AccountManagement';
import BillingSettings from './settings/BillingSettings';
import CustomBranding from './settings/CustomBranding';
import CustomFields from './settings/CustomFields';
import EmailTemplatesSettings from './settings/EmailTemplatesSettings';
import NotificationsSettings from './settings/NotificationsSettings';
import SecuritySettings from './settings/SecuritySettings';
import DeveloperSettings from './settings/DeveloperSettings';

const NAV_FIRM_IDS = [
  'company',
  'team-organization',
  'billing-settings',
  'custom-branding',
  'email-templates',
  'integrations',
  'developer',
  'tags',
];

const NAV_ACCOUNT_IDS = ['profile', 'notifications', 'email-calendar', 'security'];

const allMenuItems = [
  {
    id: 'company',
    label: 'Company Profile',
    icon: <Business />,
    permission: null,
    path: '/settings/company',
    component: <CompanyInformation />,
  },
  {
    id: 'access-permissions',
    label: 'Access & Permissions',
    icon: <Lock />,
    permission: 'can_access_company_permissions',
    path: '/settings/access-permissions',
    component: <AccessPermissions />,
  },
  {
    id: 'account-management',
    label: 'Account Management',
    icon: <Person />,
    permission: 'can_access_account_management',
    path: '/settings/account-management',
    component: <AccountManagement />,
  },
  {
    id: 'billing-settings',
    label: 'Billing & Seats',
    icon: <AccountBalance />,
    permission: 'can_access_billing_settings',
    path: '/settings/billing-settings',
    component: <BillingSettings />,
  },
  {
    id: 'custom-branding',
    label: 'Custom Branding',
    icon: <Palette />,
    permission: 'can_access_custom_branding',
    path: '/settings/custom-branding',
    component: <CustomBranding />,
  },
  {
    id: 'custom-fields',
    label: 'Custom Fields',
    icon: <List />,
    permission: 'can_access_custom_fields',
    path: '/settings/custom-fields',
    component: <CustomFields />,
  },
  {
    id: 'tags',
    label: 'Tags',
    icon: <Label />,
    permission: 'can_access_tags',
    path: '/settings/tags',
    component: <AddTagPage />,
  },
  {
    id: 'team-organization',
    label: 'Team Members',
    icon: <Groups />,
    permission: 'can_access_team_organization',
    path: '/settings/team-organization',
    component: <TeamOrganization />,
  },
  {
    id: 'profile',
    label: 'My Profile',
    icon: <AccountCircle />,
    permission: 'can_access_profile',
    path: '/settings/profile',
    component: <Profile />,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <NotificationsNone />,
    permission: 'can_access_profile',
    path: '/settings/notifications',
    component: <NotificationsSettings />,
  },
  {
    id: 'email-calendar',
    label: 'Email & Calendar',
    icon: <Email />,
    permission: 'can_access_email_calendar',
    path: '/settings/email-calendar',
    component: <EmailCalendarSettings />,
  },
  {
    id: 'email-templates',
    label: 'Email Templates',
    icon: <DescriptionIcon />,
    permission: 'can_access_email_calendar',
    path: '/settings/email-templates',
    component: <EmailTemplatesSettings />,
  },
  {
    id: 'security',
    label: 'Security',
    icon: <Https />,
    permission: 'can_access_profile',
    path: '/settings/security',
    component: <SecuritySettings />,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: <SettingsInputAntenna />,
    permission: 'can_access_integrations',
    path: '/settings/integrations',
    component: <IntegrationsSettings />,
  },
  {
    id: 'developer',
    label: 'Developer',
    icon: <CodeIcon />,
    permission: 'can_access_integrations',
    path: '/settings/developer',
    component: <DeveloperSettings />,
  },
  {
    id: 'quickbooks',
    label: 'QuickBooks',
    icon: <AccountBalance />,
    permission: 'can_access_integrations',
    path: '/settings/quickbooks',
    component: <QuickBooksManagePage />,
    hideFromSidebar: true,
  },
];

const navItemSx = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  px: 2,
  py: 1.25,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  borderLeft: '3px solid',
  borderColor: active ? 'primary.main' : 'transparent',
  bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
  color: active ? 'primary.main' : 'text.primary',
  transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
  '&:hover': {
    bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'action.hover',
  },
  '& .settings-nav-icon': {
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 20, color: active ? 'primary.main' : 'text.secondary' },
  },
});

function SettingsPanelContent() {
  const { error, isLoading, userPermissions } = useContext(AuthContext);
  const { isDirty, setIsDirty } = useSettingsForm();
  const [activeTab, setActiveTab] = useState('company');
  const [pendingNav, setPendingNav] = useState(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const pathSetting = segments[segments.length - 1] || 'company';
    if (pathSetting === 'settings') {
      navigate('/settings/company', { replace: true });
      return;
    }
    setActiveTab(pathSetting);
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const doNavigate = (path) => {
    setIsDirty(false);
    setActiveTab(path.split('/').pop());
    navigate(path);
    setPendingNav(null);
    setUnsavedOpen(false);
  };

  const handleSettingClick = (setting) => {
    if (setting.id === activeTab) return;
    if (isDirty) {
      setPendingNav(setting);
      setUnsavedOpen(true);
      return;
    }
    setActiveTab(setting.id);
    navigate(setting.path);
  };

  const handleDiscard = () => {
    if (pendingNav) {
      doNavigate(pendingNav.path);
    } else {
      setUnsavedOpen(false);
      setPendingNav(null);
    }
  };

  const handleStay = () => {
    setUnsavedOpen(false);
    setPendingNav(null);
  };

  const handleOptionSelect = () => {};

  const menuItems = useMemo(() => {
    const hasPermissions = userPermissions && Object.keys(userPermissions).length > 0;
    if (!hasPermissions) return allMenuItems;
    return allMenuItems.filter((item) => item.permission === null || userPermissions[item.permission]);
  }, [userPermissions]);

  const orgItems = menuItems.filter((i) => NAV_FIRM_IDS.includes(i.id) && !i.hideFromSidebar);
  const accountItems = menuItems.filter((i) => NAV_ACCOUNT_IDS.includes(i.id) && !i.hideFromSidebar);

  useEffect(() => {
    if (isLoading || Object.keys(userPermissions || {}).length === 0) return;
    const activeItem = menuItems.find((item) => item.id === activeTab);
    if (!activeItem && menuItems.length > 0) {
      navigate(menuItems[0].path);
    }
  }, [activeTab, menuItems, isLoading, userPermissions, navigate]);

  const activeItem = menuItems.find((item) => item.id === activeTab);
  const activeComponent = activeItem ? (
    activeItem.component
  ) : menuItems.length > 0 ? (
    <Typography>Select a setting</Typography>
  ) : (
    <Alert severity="error">You do not have access to any settings</Alert>
  );

  if (error) return <Typography color="error">{error}</Typography>;
  if (isLoading) return <CircularProgress />;

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Sidebar onOptionSelect={handleOptionSelect} />
        <Box sx={{ flex: 1, ml: '80px', display: 'flex', flexDirection: 'column', width: '100%' }}>
          <TopBar />
          <Box sx={{ height: TOP_BAR_HEIGHT, flexShrink: 0 }} />
          <Box sx={{ display: 'flex', width: '100%', flex: 1 }}>
            <Box
              component="nav"
              aria-label="Settings sections"
              sx={{
                width: 260,
                minWidth: 260,
                flexShrink: 0,
                bgcolor: 'background.paper',
                borderRight: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: TOP_BAR_HEIGHT,
                maxHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
                overflowY: 'auto',
                overflowX: 'hidden',
                py: 3,
              }}
            >
              <Typography
                component="div"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Firm
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                {orgItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleSettingClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSettingClick(item);
                      }
                    }}
                    sx={navItemSx(activeTab === item.id)}
                  >
                    <span className="settings-nav-icon">{item.icon}</span>
                    {item.label}
                  </Box>
                ))}
              </Box>

              <Typography
                component="div"
                sx={{
                  px: 2,
                  py: 1,
                  mt: 3,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Account
              </Typography>
              <Box>
                {accountItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleSettingClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSettingClick(item);
                      }
                    }}
                    sx={navItemSx(activeTab === item.id)}
                  >
                    <span className="settings-nav-icon">{item.icon}</span>
                    {item.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              component="main"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                py: 4,
                px: { xs: 2, sm: 3, md: 5 },
                bgcolor: 'background.default',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {activeComponent}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={unsavedOpen} onClose={handleStay}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStay}>Stay</Button>
          <Button onClick={handleDiscard} color="error" variant="contained">
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function SettingsPanel() {
  return (
    <SettingsFormProvider>
      <SettingsPanelContent />
    </SettingsFormProvider>
  );
}

export default SettingsPanel;
